/** UI module. Use UIManager. Public: create({ type, id, ... }); returns IJoystick | IButton | IHUD. */

import { JoystickUIManager } from './managers/JoystickUIManager';
import type { IUIManager } from './managers/IUIManager';
import type {
    UIElement,
    IJoystick,
    IButton,
    IHUD
} from './types';
import { UIElementType } from './types';

export class UIManager {
    static readonly Type = UIElementType;
    private readonly managers = new Map<UIElementType, IUIManager>();

    constructor() {
        this.injectGlobalStyles();
    }

    /** Create UI element by type; returns IJoystick | IButton | IHUD. */
    create(type: UIElementType.Joystick): IUIManager<IJoystick>;
    create(type: UIElementType.Button): IUIManager<IButton>;
    create(type: UIElementType.HUD): IUIManager<IHUD>;
    create(type: UIElementType): IUIManager<UIElement> {
        switch (type) {
            case 'joystick': {
                // Create manager only when first joystick is created
                if (!this.managers.has(UIElementType.Joystick)) {
                    this.managers.set(UIElementType.Joystick, new JoystickUIManager());
                }
                const manager = this.managers.get(UIElementType.Joystick) as JoystickUIManager;
                return manager;
    }

            case 'button':
                // Future: Create manager only when first button is created
                // if (!this.managers.has(UIElementType.Button)) {
                //     this.managers.set(UIElementType.Button, new ButtonUIManager());
                // }
                // const manager = this.managers.get(UIElementType.Button) as ButtonUIManager;
                // return manager.create(config);
                throw new Error('Button UI not yet implemented. Coming soon!');

            case 'hud':
                // Future: Create manager only when first HUD is created
                // if (!this.managers.has(UIElementType.HUD)) {
                //     this.managers.set(UIElementType.HUD, new HUDUIManager());
                // }
                // const manager = this.managers.get(UIElementType.HUD) as HUDUIManager;
                // return manager.create(config);
                throw new Error('HUD UI not yet implemented. Coming soon!');

            default:
                // TypeScript will ensure this is never reached
                const _exhaustive = type;
                throw new Error(`Unknown UI element type: ${_exhaustive}`);
        }
    }

    // ================== GET METHODS ==================

    /**
     * Get an existing UI element by ID
     * Iterates through all active managers to find the element
     * 
     * @param id - Element ID
     * @returns UI element instance or null if not found
     * 
     * @example
     * ```typescript
     * const joystick = uiManager.get('movement');
     * if (joystick) {
     *   joystick.update(vector, isActive);
     * }
     * ```
     */
    get(id: string): UIElement | null {
        // Iterate through all active managers
        for (const manager of this.managers.values()) {
            const element = manager.get(id);
            if (element) return element;
        }
        return null;
    }

    // ================== CLEANUP ==================

    /**
     * Remove a specific UI element by ID
     * Iterates through all active managers to find and remove the element
     * 
     * @param id - Element ID
     * @returns True if element was found and removed, false otherwise
     * 
     * @example
     * ```typescript
     * // Remove a specific joystick
     * const removed = uiManager.remove('movement');
     * if (removed) {
     *   console.log('Joystick removed successfully');
     * }
     * 
     * // Or remove directly from the instance
     * const joystick = uiManager.get('movement');
     * joystick?.remove();
     * uiManager.remove('movement'); // Clean up from manager's Map
     * ```
     */
    remove(id: string): boolean {
        // Iterate through all active managers
        for (const manager of this.managers.values()) {
            if (manager.remove(id)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Dispose all UI components
     * Iterates through all active managers and disposes them
     */
    dispose(): void {
        for (const manager of this.managers.values()) {
            manager.dispose();
        }
        this.managers.clear();
    }

    // ================== HELPERS ==================

    /**
     * Inject global CSS styles for UI components
     */
    private injectGlobalStyles(): void {
        if (document.getElementById('plutus-ui-styles')) {
            return; // Already injected
        }

        const style = document.createElement('style');
        style.id = 'plutus-ui-styles';
        style.textContent = `
            /* Plutus UI Module - Global Styles */
            .plutus-joystick-container {
                user-select: none;
                -webkit-user-select: none;
                -webkit-tap-highlight-color: transparent;
                touch-action: none;
            }

            .plutus-joystick-base,
            .plutus-joystick-knob {
                pointer-events: auto;
            }

            /* Prevent scrolling on touch devices when using joystick */
            body.plutus-joystick-active {
                overflow: hidden;
                position: fixed;
                width: 100%;
                height: 100%;
            }

            /* Hide scrollbars during joystick use */
            .plutus-joystick-container:active {
                overflow: hidden;
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Check if device is mobile/touch-enabled
     */
    isMobileDevice(): boolean {
        return (
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        );
    }

    /**
     * Enable/disable touch scroll prevention globally
     */
    setTouchScrollPrevention(enabled: boolean): void {
        if (enabled) {
            document.body.classList.add('plutus-joystick-active');
        } else {
            document.body.classList.remove('plutus-joystick-active');
        }
    }
}
